import React from "react";
import { Container } from "../styled.ts";
import { Typography } from "../typography/index.tsx";
import { canChooseAllCompanies, coverageForDisplay, isTelegramChannel } from "../../utils/stockCoverage.ts";

type Coverage = "watchlist" | "all";

interface StockCoverageChoiceProps {
  channel?: string | null;
  plan?: string | null;
  coverage?: string | null;
  saving?: boolean;
  onChange: (coverage: Coverage) => void;
}

const choiceStyle = (selected: boolean, locked: boolean): React.CSSProperties => ({
  flex: "1 1 140px",
  minHeight: "44px",
  padding: "10px 12px",
  borderRadius: "12px",
  border: selected ? "solid 1px transparent" : "solid 1px #E4E4E4",
  background: selected ? "linear-gradient(165.56deg, #5395FF 39.75%, #D69DFF 58.69%)" : "#fff",
  color: selected ? "#fff" : "#202020",
  cursor: locked ? "default" : "pointer",
  fontFamily: "Neue Montreal, sans-serif",
  fontSize: "14px",
  fontWeight: 500,
  opacity: locked && !selected ? 0.45 : 1,
});

export const StockCoverageChoice = ({
  channel,
  plan,
  coverage,
  saving = false,
  onChange,
}: StockCoverageChoiceProps) => {
  if (!isTelegramChannel(channel)) {
    return null;
  }

  const selected = coverageForDisplay(channel, plan, coverage);
  const planLocksAll = !canChooseAllCompanies(channel, plan);
  const allLocked = planLocksAll || saving;

  const choose = (next: Coverage) => {
    if (saving || next === selected) {
      return;
    }
    if (next === "all" && !canChooseAllCompanies(channel, plan)) {
      return;
    }
    onChange(next);
  };

  return (
    <Container column gap="10px" width="100%">
      <Typography
        tag="p"
        fontFamily="Neue Montreal"
        fontSize="20px"
        fontWeight={500}
        lineHeight="30px"
        letterSpacing="0.02em"
        color="#202020"
      >
        Stocks you follow
      </Typography>
      <Container gap="8px" wrap width="100%">
        <button type="button" style={choiceStyle(selected === "watchlist", saving)} onClick={() => choose("watchlist")}>
          My watchlist
        </button>
        <button
          type="button"
          style={choiceStyle(selected === "all", allLocked)}
          onClick={() => choose("all")}
          disabled={allLocked && selected !== "all"}
        >
          All companies
        </button>
      </Container>
      <Typography
        tag="p"
        fontFamily="Neue Montreal"
        fontSize="14px"
        fontWeight={400}
        lineHeight="21px"
        letterSpacing="0.02em"
        color="#828282"
      >
        {selected === "all"
          ? "You hear about every company that files. The stocks you saved stay on your watch list."
          : planLocksAll
            ? "Every company is available on Trial and paid plans."
            : "You hear about a filing only when it is one of your stocks."}
      </Typography>
    </Container>
  );
};
